import { useState } from 'react'
import {
  Card, Group, Text, Badge, Button, Progress, Skeleton, Stack,
  Table, Collapse, Box, ThemeIcon,
} from '@mantine/core'
import { IconAlertTriangle, IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { useZabbixMetrics } from '@/hooks/useZabbixMetrics'
import { SEVERITY_COLORS, SEVERITY_ORDER } from '@/pages/dashboard/zabbixConstants'
import type { ZabbixProblem } from '@/hooks/useZabbixMetrics'

interface ZabbixOverviewProps {
  tenantId: string
}

function elapsedTime(startedAt: string | null): string {
  if (!startedAt) return '—'
  const diff = Date.now() - new Date(startedAt).getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 60) return `${minutes}min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d ${hours % 24}h`
}

function sortBySeverity(a: ZabbixProblem, b: ZabbixProblem): number {
  return (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99)
}

function ProblemsTable({ problems }: { problems: ZabbixProblem[] }) {
  if (problems.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="sm">
        Nenhum problema para esta severidade.
      </Text>
    )
  }
  return (
    <Table striped highlightOnHover fz="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Severidade</Table.Th>
          <Table.Th>Host</Table.Th>
          <Table.Th>Problema</Table.Th>
          <Table.Th>Duração</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {problems.map(p => (
          <Table.Tr key={p.id}>
            <Table.Td>
              <Badge color={SEVERITY_COLORS[p.severity] ?? 'gray'} size="sm" tt="uppercase">
                {p.severity}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Text size="sm" fw={500}>{p.host || '—'}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{p.name}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm" c="dimmed">{elapsedTime(p.started_at)}</Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

export function ZabbixOverview({ tenantId }: ZabbixOverviewProps) {
  const { data, isLoading, refresh } = useZabbixMetrics(tenantId)
  const [cooldown, setCooldown] = useState(false)
  const [activeSeverity, setActiveSeverity] = useState<string | null>(null)

  const handleRefresh = () => {
    refresh()
    setCooldown(true)
    setTimeout(() => setCooldown(false), 60_000)
  }

  const toggleSeverity = (severity: string) => {
    setActiveSeverity(prev => (prev === severity ? null : severity))
  }

  if (isLoading) {
    return (
      <Card withBorder shadow="sm" radius="md" p="lg">
        <Stack gap="md">
          <Skeleton height={20} width="40%" />
          <Skeleton height={40} />
          <Skeleton height={16} />
          <Skeleton height={80} />
        </Stack>
      </Card>
    )
  }

  if (!data) return null

  const { summary, collected_at, is_stale } = data
  const { total_hosts, hosts_up, hosts_down, hosts_disabled = 0, problems_by_severity } = summary

  // Health % calculated over monitored hosts only (excludes disabled)
  const monitoredHosts = total_hosts - hosts_disabled
  const healthPct = monitoredHosts > 0 ? (hosts_up / monitoredHosts) * 100 : 0
  const healthColor = healthPct >= 90 ? 'green' : healthPct >= 70 ? 'yellow' : 'red'

  const allProblems = [...(data.problems ?? [])].sort(sortBySeverity)
  const filteredProblems = activeSeverity
    ? allProblems.filter(p => p.severity === activeSeverity)
    : allProblems

  const totalProblems = allProblems.length

  return (
    <Card withBorder shadow="sm" radius="md" p="lg">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            <Text fw={600} size="lg">Monitoramento Zabbix</Text>
            {is_stale && <Badge color="orange">Dados desatualizados</Badge>}
          </Group>
          <Group gap="sm">
            <Text size="xs" c="dimmed">
              {new Date(collected_at).toLocaleString('pt-BR')}
            </Text>
            <Button
              size="xs"
              variant="light"
              disabled={cooldown || isLoading}
              onClick={handleRefresh}
            >
              Atualizar
            </Button>
          </Group>
        </Group>

        {/* Hosts row */}
        <Group gap="md">
          <Text size="sm" c="green" fw={600}>{hosts_up} hosts up</Text>
          <Text size="sm" c="red" fw={600}>{hosts_down} hosts down</Text>
          {hosts_disabled > 0 && (
            <Text size="sm" c="dimmed" fw={600}>{hosts_disabled} desabilitados</Text>
          )}
        </Group>

        {/* Health bar */}
        <div>
          <Progress value={healthPct} color={healthColor} size="lg" radius="md" />
          <Text size="xs" c="dimmed" mt={4}>
            {Math.round(healthPct)}% hosts saudáveis
          </Text>
        </div>

        {/* Severity badges — clicáveis para filtrar */}
        <Group gap="xs">
          {Object.entries(SEVERITY_COLORS).map(([severity, color]) => {
            const count = problems_by_severity[severity] ?? 0
            const isActive = activeSeverity === severity
            const hasProblems = count > 0
            return (
              <Badge
                key={severity}
                color={color}
                variant={isActive ? 'filled' : hasProblems ? 'filled' : 'light'}
                style={{
                  opacity: !hasProblems ? 0.5 : 1,
                  cursor: hasProblems ? 'pointer' : 'default',
                  outline: isActive ? `2px solid var(--mantine-color-${color}-6)` : 'none',
                  outlineOffset: 2,
                }}
                rightSection={hasProblems
                  ? isActive ? <IconChevronUp size={10} /> : <IconChevronDown size={10} />
                  : null
                }
                onClick={() => hasProblems && toggleSeverity(severity)}
              >
                {severity}: {count}
              </Badge>
            )
          })}
          {totalProblems > 0 && (
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => setActiveSeverity(activeSeverity === '__all__' ? null : '__all__')}
              rightSection={activeSeverity === '__all__' ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
            >
              Ver todos ({totalProblems})
            </Button>
          )}
        </Group>

        {/* Inline problems table */}
        <Collapse in={activeSeverity !== null}>
          <Box
            style={{
              border: '1px solid var(--mantine-color-dark-4)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <Group gap="xs" p="sm" bg="var(--mantine-color-dark-7)">
              <ThemeIcon
                size="sm"
                color={activeSeverity && activeSeverity !== '__all__' ? SEVERITY_COLORS[activeSeverity] : 'gray'}
                variant="light"
              >
                <IconAlertTriangle size={12} />
              </ThemeIcon>
              <Text size="sm" fw={600}>
                {activeSeverity === '__all__'
                  ? `Todos os problemas (${totalProblems})`
                  : `Problemas ${activeSeverity?.toUpperCase()} (${problems_by_severity[activeSeverity ?? ''] ?? 0})`
                }
              </Text>
            </Group>
            <Box p="sm">
              <ProblemsTable problems={filteredProblems} />
            </Box>
          </Box>
        </Collapse>
      </Stack>
    </Card>
  )
}
